import { useEffect } from 'react'

/**
 * MarketLimitToggle — neon rocker switch (Uiverse by preet_7613, adapted).
 *
 * Unchecked (knob left, red LED)  = MARKET
 * Checked   (knob right, green LED) = LIMIT
 *
 *   <MarketLimitToggle
 *     mode={orderMode}                         // 'market' | 'limit'
 *     onChange={(m) => setOrderMode(m)}
 *     scale={0.42} />
 *
 * The neon SVGs use pathLength normalization so the exact stroke-dasharray
 * values from the original CSS animate correctly regardless of path geometry.
 * Native switch is 100×60; `scale` shrinks it to fit compact toolbars.
 */

const CSS_ID = 'market-limit-switch-css'
const CSS = `
.mlswitch {
  display: block;
  position: relative;
  width: 100px;
  height: 60px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.mlswitch__base-outer,
.mlswitch__base-inner { display: block; position: absolute; }
.mlswitch__base-outer {
  border-radius: 25px;
  box-shadow:
    -2px -2px 4px hsl(223, 10%, 30%),
    2px 2px 2px hsl(223, 10%, 30%) inset,
    2px 2px 4px hsl(0, 0%, 0%),
    -2px -2px 2px hsl(223, 10%, 5%) inset;
  top: 2.5px; left: 2.5px; width: 95px; height: 55px;
}
.mlswitch__base-inner {
  border-radius: 21px;
  box-shadow:
    -4px -4px 4px hsl(223, 10%, 30%) inset,
    1px 1px 2px hsla(223, 10%, 30%, 1),
    2px 4px 4px hsl(223, 10%, 5%) inset,
    -1px -1px 2px hsla(223, 10%, 5%, 1);
  top: 7.5px; left: 7.5px; width: 85px; height: 45px;
}
.mlswitch__base-neon {
  display: block; overflow: visible; position: absolute;
  top: 0; left: 0; width: 100%; height: auto;
}
.mlswitch__base-neon path {
  stroke-dasharray: 0 104.26 0;
  transition: stroke-dasharray 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.mlswitch__input {
  outline: transparent; position: relative; width: 100%; height: 100%;
  -webkit-appearance: none; appearance: none; cursor: pointer; margin: 0;
}
.mlswitch__knob-container {
  border-radius: 20px; display: block; position: absolute; overflow: hidden;
  top: 10px; left: 10px; width: 80px; height: 40px;
}
.mlswitch__knob {
  border-radius: 20px; display: block; position: absolute;
  background-color: hsl(223, 10%, 15%);
  background-image: radial-gradient(88% 88% at 50% 50%, hsl(223, 10%, 20%) 47%, hsla(223, 10%, 20%, 0) 50%),
    radial-gradient(88% 88% at 47% 47%, hsl(223, 10%, 85%) 45%, hsla(223, 10%, 85%, 0) 50%),
    radial-gradient(65% 70% at 40% 60%, hsl(223, 10%, 20%) 46%, hsla(223, 10%, 20%, 0) 50%);
  box-shadow:
    -1px -1px 1px hsl(223, 10%, 15%) inset,
    -2px -2px 1px hsl(223, 10%, 5%) inset,
    12px 4px 2px hsla(0, 0%, 0%, 0.8);
  width: 40px; height: 40px;
  transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.mlswitch__knob-neon { display: block; width: 40px; height: auto; }
.mlswitch__knob-neon circle {
  opacity: 0;
  stroke-dasharray: 0 90.32 0 54.19;
  transition: opacity 0.6s steps(1, end), stroke-dasharray 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.mlswitch__knob-shadow {
  border-radius: 50%; box-shadow: 2px 2px 2px hsla(0, 0%, 0%, 0.9);
  display: block; position: absolute; top: 10px; left: 10px; width: 40px; height: 40px;
  transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.mlswitch__led {
  background-color: hsl(3, 90%, 70%); border-radius: 50%;
  box-shadow:
    0 -1px 1px hsl(3, 90%, 40%) inset,
    0 0 2px hsla(3, 90%, 70%, 0.3),
    2px 2px 2px hsla(0, 0%, 0%, 0.5);
  display: block; position: absolute; top: 0; left: 0; width: 5px; height: 5px;
  transition: background-color 0.6s cubic-bezier(0.65, 0, 0.35, 1), box-shadow 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.mlswitch__text { overflow: hidden; position: absolute; width: 1px; height: 1px; }
.mlswitch__input:checked ~ .mlswitch__led {
  background-color: hsl(123, 90%, 70%);
  box-shadow:
    0 -1px 1px hsl(123, 90%, 40%) inset,
    0 -2px 2px hsla(123, 90%, 70%, 0.3),
    0 2px 2px hsla(123, 90%, 70%, 0.3),
    2px 2px 2px hsla(0, 0%, 0%, 0.5);
}
.mlswitch__input:checked ~ .mlswitch__base-neon path { stroke-dasharray: 52.13 0 52.13; }
.mlswitch__input:checked ~ .mlswitch__knob-shadow,
.mlswitch__input:checked ~ .mlswitch__knob-container .mlswitch__knob { transform: translateX(40px); }
.mlswitch__input:checked ~ .mlswitch__knob-container .mlswitch__knob-neon circle {
  opacity: 1;
  stroke-dasharray: 45.16 0 45.16 54.19;
  transition-timing-function: steps(1, start), cubic-bezier(0.65, 0, 0.35, 1);
}
`

export default function MarketLimitToggle({
  mode = 'market',
  onChange,
  scale = 0.42,
  leftLabel = 'MKT',
  rightLabel = 'LMT',
  showLabels = true,
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (document.getElementById(CSS_ID)) return
    const el = document.createElement('style')
    el.id = CSS_ID
    el.textContent = CSS
    document.head.appendChild(el)
  }, [])

  const checked = mode === 'limit'
  const neon = 'hsl(123, 90%, 70%)' // green neon, matches the LED's ON state

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, userSelect: 'none' }}>
      {showLabels && (
        <span style={{
          fontSize: 8, fontWeight: 700, letterSpacing: 0.5,
          fontFamily: "'JetBrains Mono', monospace",
          color: checked ? '#555' : '#ffb020', transition: 'color 0.25s',
        }}>{leftLabel}</span>
      )}

      {/* Scale wrapper: reserves the shrunk footprint, scales the switch inside */}
      <span style={{
        display: 'inline-block',
        width: 100 * scale, height: 60 * scale, flex: '0 0 auto',
      }}>
        <label
          className="mlswitch"
          style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
          title={`Order type: ${checked ? 'LIMIT' : 'MARKET'}`}
        >
          <input
            className="mlswitch__input"
            type="checkbox"
            role="switch"
            checked={checked}
            onChange={(e) => onChange && onChange(e.target.checked ? 'limit' : 'market')}
          />
          <span className="mlswitch__base-outer" />
          <span className="mlswitch__base-inner" />
          <svg className="mlswitch__base-neon" viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg">
            <path
              pathLength="104.26"
              d="M 28.5 7.5 H 71.5 A 21 21 0 0 1 71.5 52.5 H 28.5 A 21 21 0 0 1 28.5 7.5 Z"
              fill="none" stroke={neon} strokeWidth="1.5" strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 3px ${neon})` }}
            />
          </svg>
          <span className="mlswitch__led" />
          <span className="mlswitch__knob-shadow" />
          <span className="mlswitch__knob-container">
            <span className="mlswitch__knob">
              <svg className="mlswitch__knob-neon" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle
                  pathLength="144.51"
                  cx="20" cy="20" r="15"
                  fill="none" stroke={neon} strokeWidth="2" strokeLinecap="round"
                  style={{ filter: `drop-shadow(0 0 2px ${neon})` }}
                />
              </svg>
            </span>
          </span>
          <span className="mlswitch__text">Market / Limit</span>
        </label>
      </span>

      {showLabels && (
        <span style={{
          fontSize: 8, fontWeight: 700, letterSpacing: 0.5,
          fontFamily: "'JetBrains Mono', monospace",
          color: checked ? '#00ff88' : '#555', transition: 'color 0.25s',
        }}>{rightLabel}</span>
      )}
    </span>
  )
}
